import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TreeModule } from 'primeng/tree';
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay } from 'rxjs';
import { DestinationFilter, YoungAdultsPriceList, YoungAdultsPriceLists } from '../../models';
import { CompanyEnum } from '../../models/company.model';
import { FilterService } from '../../services/filter.service';
import { SharedDataStore } from '../../store';
import { PriceListPanelComponent } from '../price-list-panel/price-list-panel.component';
import { YoungAdultsPriceListDataComponent } from '../young-adults-pricelist-data/young-adults-price-list-data.component';

@Component({
  standalone: true,
  selector: 'lx-price-lists-portal-young-adults',
  imports: [CommonModule, ProgressSpinnerModule, TreeModule, TableModule, PriceListPanelComponent, YoungAdultsPriceListDataComponent],
  templateUrl: './young-adults.component.html',
  styleUrls: ['./young-adults.component.scss'],
})
export class YoungAdultsComponent implements OnInit {
  protected company = CompanyEnum.KIL;
  protected youngAdultsSchools$: Observable<YoungAdultsPriceLists> | undefined;
  protected youngAdultsFilters$: Observable<DestinationFilter[]> | undefined;

  private filtersSubject = new BehaviorSubject<DestinationFilter[]>([]);
  private selectedFilters$ = this.filtersSubject.asObservable();

  private readonly filterService = inject(FilterService);
  private readonly sharedDataStore = inject(SharedDataStore);

  ngOnInit(): void {
    const youngAdultsSchools$ = this.sharedDataStore.youngAdultsPriceLists$.pipe(shareReplay(1));
    this.youngAdultsFilters$ = youngAdultsSchools$.pipe(
      map((youngAdultsSchools) => this.filterService.getYoungAdultsSchoolsDestinationFilters(youngAdultsSchools) || []),
    );
    this.initializeFilteredSchools(youngAdultsSchools$);
  }

  filterYoungAdultsSchools(filters: DestinationFilter | DestinationFilter[]): void {
    this.filtersSubject.next(Array.isArray(filters) ? filters : [filters]);
  }

  /**
   * Initializes the observable for filtered young adult schools based on the selected filters.
   * @param youngAdultsSchools$ the observable containing the young adults school data from API
   */
  private initializeFilteredSchools(youngAdultsSchools$: Observable<YoungAdultsPriceLists>) {
    this.youngAdultsSchools$ = combineLatest([youngAdultsSchools$ || of({ schools: [] }), this.selectedFilters$]).pipe(
      map(([youngAdultsSchools, selectedFilters]) => {
        if (!youngAdultsSchools || !youngAdultsSchools.schools?.length) {
          return { schools: [] };
        }

        if (!selectedFilters?.length) {
          return { ...youngAdultsSchools };
        }

        const filteredSchools = youngAdultsSchools.schools.filter(
          ({
            youngAdultSchoolInfo: {
              country: { code: countryCode },
              school: { code: schoolCode },
            },
          }: YoungAdultsPriceList) =>
            (countryCode && selectedFilters.some((filter) => countryCode === filter.key)) ||
            (schoolCode && selectedFilters.some((filter) => schoolCode === filter.key)),
        );

        return {
          schools: filteredSchools,
        };
      }),
    );
  }
}
